import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Hidden,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LinkToShare } from "./apps-types";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import { QRCodeSection } from "../download-components/DesktopComponents";

export default function LinkSummaryDialog(props: {
  open: boolean;
  setOpen: any;
  generatedLinkText: string;
  generatedLink: LinkToShare | undefined | null;
  name: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const tooltipLabels = [t("Copy to clipboard"), t("Open URL")];

  const handleCloseDialog = () => {
    props.setOpen(false);
  };

  return (
    <Box>
      <Dialog
        open={props.open}
        onClose={handleCloseDialog}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t("Share your link")}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {props.generatedLink === undefined || props.generatedLink === null ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                height="100%x"
              >
                <CircularProgress size={80} thickness={3} />
                <Typography variant="h5" textAlign="center">
                  {t("Link generation in progress...")}
                </Typography>
              </Stack>
            </Box>
          ) : (
            props.generatedLink !== undefined &&
            props.generatedLink !== null && (
              <Box>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 2 }}
                >
                  <Hidden mdDown>
                    <Grid item md={12}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ width: "100%" }}
                      >
                        <QRCodeSection link={props.generatedLinkText} />
                      </Box>
                    </Grid>
                  </Hidden>
                  <Grid item xs={12} sm={10}>
                    <TextField
                      margin="dense"
                      id="link"
                      label={t("Generated link")}
                      fullWidth
                      variant="outlined"
                      value={props.generatedLinkText}
                    />
                  </Grid>
                  <Hidden smDown>
                    <Grid item xs={1}>
                      <Tooltip title={tooltipLabels[0]} arrow>
                        <ToggleButton
                          value="copy"
                          size="large"
                          onClick={() =>
                            navigator.clipboard.writeText(
                              props.generatedLinkText
                            )
                          }
                        >
                          <ContentCopyIcon />
                        </ToggleButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={1}>
                      <Tooltip title={tooltipLabels[1]} arrow>
                        <ToggleButton
                          value="copy"
                          size="large"
                          onClick={() =>
                            window.open(props.generatedLinkText, "_blank")
                          }
                        >
                          <LaunchIcon />
                        </ToggleButton>
                      </Tooltip>
                    </Grid>
                  </Hidden>
                  <Hidden smUp>
                    <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() =>
                          navigator.clipboard.writeText(props.generatedLinkText)
                        }
                      >
                        {t("Copy link")}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<LaunchIcon />}
                        onClick={() =>
                          window.open(props.generatedLinkText, "_blank")
                        }
                      >
                        {t("Open URL")}
                      </Button>
                    </Stack>
                  </Hidden>
                </Grid>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      id="app-name"
                      label={t("Application")}
                      fullWidth
                      variant="standard"
                      value={props.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      id="version"
                      label={t("Version")}
                      fullWidth
                      variant="standard"
                      value={
                        props.generatedLink.versions.length > 1
                          ? props.generatedLink.versions[0].platform +
                            ": " +
                            props.generatedLink.versions[0].versionNumber +
                            " - " +
                            props.generatedLink.versions[1].platform +
                            ": " +
                            props.generatedLink.versions[1].versionNumber
                          : props.generatedLink.versions[0].platform +
                            ": " +
                            props.generatedLink.versions[0].versionNumber
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      id="validity-start"
                      label={t("Validity start")}
                      fullWidth
                      variant="standard"
                      value={new Date(
                        props.generatedLink.validity_start
                      ).toLocaleString()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      id="validity-end"
                      label={t("Validity end")}
                      fullWidth
                      variant="standard"
                      value={new Date(
                        props.generatedLink.validity_end
                      ).toLocaleString()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      margin="dense"
                      id="duration"
                      label={t("Minutes remaining")}
                      fullWidth
                      inputProps={{ min: 0 }}
                      variant="standard"
                      value={
                        Math.round(
                          (new Date(
                            props.generatedLink.validity_end
                          ).getTime() -
                            new Date().getTime()) /
                            60000
                        ) > 0
                          ? Math.round(
                              (new Date(
                                props.generatedLink.validity_end
                              ).getTime() -
                                new Date().getTime()) /
                                60000
                            )
                          : t("Expired")
                      }
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      margin="dense"
                      id="max-downloads"
                      label={t("Max downloads")}
                      fullWidth
                      variant="standard"
                      value={props.generatedLink.maxdownloads}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      margin="dense"
                      id="authentication"
                      label={t("Authentication")}
                      fullWidth
                      variant="standard"
                      value={
                        props.generatedLink.isAuthenticated ? t("Yes") : t("No")
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
